// frontend/src/admin/v2/hooks/useSecrets.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

/**
 * useSecrets — fetches /api/admin/secrets and exposes markRotated().
 *
 * Returns { data, loading, error, errorTitle, markRotated, refetch }.
 *   markRotated(envVar, note?) → POST /api/admin/secrets/<env>/mark-rotated,
 *                                refetches list on success.
 *
 * 🔴 Welle 4 PR 1 (§4.1): `error` ist der Nutzersatz, `errorTitle` traegt das
 * Entwicklerdetail (den Env-Namen) fuer den Tooltip.
 */
export function useSecrets() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorTitle, setErrorTitle] = useState(null)
  const inflightRef = useRef(null)

  const fetchAll = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/secrets`, {
        headers: HEADERS,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
      setErrorTitle(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      const isKey = e.response?.status === 403
      setError(isKey ? 'Admin data is unavailable in this build.' : (e.message || 'Failed to load'))
      setErrorTitle(isKey ? 'REACT_APP_ADMIN_KEY' : null)
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [])

  const markRotated = useCallback(async (envVar, note = '') => {
    await axios.post(`${API}/api/admin/secrets/${envVar}/mark-rotated`,
      { note }, { headers: HEADERS })
    await fetchAll()
  }, [fetchAll])

  useEffect(() => {
    fetchAll()
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchAll])

  return { data, loading, error, errorTitle, markRotated, refetch: fetchAll }
}
